import { type ChangeEvent, useEffect, useMemo, useState } from 'react';

import {
  CATEGORIES,
  formatTagsInput,
  LICENCE_CLASSES,
  parseTagsInput,
  PRIORITIES,
  requiredUrlFieldFor,
  SOURCE_TYPES,
  type SourceFormState,
} from '../lib/forms';

/**
 * Controlled form for creating / editing a feed source.
 * Mirrors `validateCreateSource` in `lib/admin/validation.ts` for client-side
 * checks; the server is the source of truth — these checks are UX, not security.
 */

export type { SourceFormState } from '../lib/forms';

const OPTIONAL_URL_FIELDS = [
  ['rss_url', 'rss_url'],
  ['youtube_channel_id', 'youtube_channel_id'],
  ['youtube_handle', 'youtube_handle'],
  ['twitter_handle', 'twitter_handle'],
  ['bluesky_handle', 'bluesky_handle'],
  ['mastodon_url', 'mastodon_url'],
  ['blog_url', 'blog_url'],
  ['newsletter_url', 'newsletter_url'],
  ['website_url', 'website_url'],
  // Sitemap-only knob: a regex that filters which sitemap URLs become feed_items.
  // Surfaced in the optional-URLs panel so it stays out of the way for non-sitemap sources.
  ['sitemap_url_filter', 'sitemap_url_filter'],
] as const;

type OptionalUrlField = (typeof OPTIONAL_URL_FIELDS)[number][0];

export function emptySourceFormState(): SourceFormState {
  return {
    name: '',
    category: '',
    priority: 'normal',
    source_type: '',
    licence_class: 'public_rss',
    poll_interval_minutes: 60,
    active: true,
    tags: [],
    expertise: null,
    rss_url: null,
    youtube_channel_id: null,
    youtube_handle: null,
    twitter_handle: null,
    bluesky_handle: null,
    mastodon_url: null,
    blog_url: null,
    newsletter_url: null,
    website_url: null,
    sitemap_url_filter: null,
  };
}

export function SourceForm({
  initial,
  submitLabel,
  busy,
  serverError,
  onSubmit,
  onCancel,
}: {
  readonly initial: SourceFormState;
  readonly submitLabel: string;
  readonly busy: boolean;
  readonly serverError: string | null;
  readonly onSubmit: (state: SourceFormState) => void;
  readonly onCancel: () => void;
}): JSX.Element {
  const [state, setState] = useState<SourceFormState>(initial);
  const [tagsRaw, setTagsRaw] = useState<string>(formatTagsInput(initial.tags));
  const [clientError, setClientError] = useState<string | null>(null);

  // Reset when `initial` identity changes (e.g. drawer opened on a different row).
  useEffect(() => {
    setState(initial);
    setTagsRaw(formatTagsInput(initial.tags));
    setClientError(null);
  }, [initial]);

  const requiredUrlField = useMemo(() => {
    if (state.source_type === '') return null;
    return requiredUrlFieldFor(state.source_type);
  }, [state.source_type]);

  const validate = (): string | null => {
    if (state.name.trim().length === 0) return 'name is required';
    if (state.name.length > 200) return 'name must be ≤200 chars';
    if (state.category === '') return 'category is required';
    if (state.source_type === '') return 'source_type is required';
    if (
      !Number.isInteger(state.poll_interval_minutes) ||
      state.poll_interval_minutes <= 0 ||
      state.poll_interval_minutes > 7 * 24 * 60
    ) {
      return 'poll_interval_minutes must be an integer in (0, 10080]';
    }
    if (requiredUrlField !== null) {
      const v = state[requiredUrlField as OptionalUrlField] as string | null;
      if (v === null || v.trim().length === 0) {
        return `${String(requiredUrlField)} is required when source_type=${state.source_type}`;
      }
    }
    return null;
  };

  const onSubmitClick = (): void => {
    const err = validate();
    if (err !== null) {
      setClientError(err);
      return;
    }
    setClientError(null);
    // Commit current tagsRaw into state.tags right before submit.
    const cleaned: SourceFormState = {
      ...state,
      name: state.name.trim(),
      tags: parseTagsInput(tagsRaw),
    };
    onSubmit(cleaned);
  };

  const setOptionalText = (field: OptionalUrlField, value: string): void => {
    setState((prev) => ({ ...prev, [field]: value === '' ? null : value }));
  };

  const setExpertise = (value: string): void => {
    setState((prev) => ({ ...prev, expertise: value === '' ? null : value }));
  };

  const onSourceTypeChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const next = e.target.value as SourceFormState['source_type'];
    setState((prev) => {
      // When the source_type changes, clear the previously-required URL field
      // so a leftover value doesn't silently mismatch the new type.
      const prevRequired =
        prev.source_type === '' ? null : requiredUrlFieldFor(prev.source_type);
      const nextRequired = next === '' ? null : requiredUrlFieldFor(next);
      if (prevRequired !== null && prevRequired !== nextRequired) {
        return { ...prev, [prevRequired as OptionalUrlField]: null, source_type: next };
      }
      return { ...prev, source_type: next };
    });
  };

  const requiredFieldLabel =
    requiredUrlField !== null ? String(requiredUrlField) : null;
  const requiredFieldValue =
    requiredUrlField !== null
      ? ((state[requiredUrlField as OptionalUrlField] as string | null) ?? '')
      : '';

  const errorToShow = clientError ?? serverError;

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmitClick(); }}
      className="space-y-4"
    >
      {errorToShow !== null && (
        <p className="text-2xs font-mono text-bad/90 border border-bad/30 bg-bad/10 px-3 py-2 rounded">
          {errorToShow}
        </p>
      )}

      <div>
        <label className="label" htmlFor="src-name">name</label>
        <input
          id="src-name"
          type="text"
          className="input w-full"
          value={state.name}
          onChange={(e) => { setState((p) => ({ ...p, name: e.target.value })); }}
          maxLength={200}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor="src-category">category</label>
          <select
            id="src-category"
            className="input w-full"
            value={state.category}
            onChange={(e) => {
              setState((p) => ({ ...p, category: e.target.value as SourceFormState['category'] }));
            }}
            required
          >
            <option value="">—</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="src-priority">priority</label>
          <select
            id="src-priority"
            className="input w-full"
            value={state.priority}
            onChange={(e) => {
              setState((p) => ({ ...p, priority: e.target.value as SourceFormState['priority'] }));
            }}
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor="src-type">source_type</label>
          <select
            id="src-type"
            className="input w-full"
            value={state.source_type}
            onChange={onSourceTypeChange}
            required
          >
            <option value="">—</option>
            {SOURCE_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="src-licence">licence_class</label>
          <select
            id="src-licence"
            className="input w-full"
            value={state.licence_class}
            onChange={(e) => {
              setState((p) => ({ ...p, licence_class: e.target.value as SourceFormState['licence_class'] }));
            }}
          >
            {LICENCE_CLASSES.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
      </div>

      {requiredFieldLabel !== null && (
        <div>
          <label className="label" htmlFor="src-required-url">
            {requiredFieldLabel} <span className="text-bad/80">*</span>
          </label>
          <input
            id="src-required-url"
            type="text"
            className="input w-full"
            value={requiredFieldValue}
            onChange={(e) => {
              setOptionalText(requiredUrlField as OptionalUrlField, e.target.value);
            }}
            required
          />
          <p className="help">required when source_type={state.source_type}</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor="src-poll">poll_interval_minutes</label>
          <input
            id="src-poll"
            type="number"
            className="input w-full"
            min={1}
            max={10080}
            step={1}
            value={state.poll_interval_minutes}
            onChange={(e) => {
              const n = Number.parseInt(e.target.value, 10);
              setState((p) => ({
                ...p,
                poll_interval_minutes: Number.isFinite(n) ? n : 0,
              }));
            }}
          />
        </div>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-sm font-mono text-ink select-none">
            <input
              type="checkbox"
              checked={state.active}
              onChange={(e) => { setState((p) => ({ ...p, active: e.target.checked })); }}
              className="accent-accent w-4 h-4"
            />
            active
          </label>
        </div>
      </div>

      <div>
        <label className="label" htmlFor="src-tags">tags</label>
        <input
          id="src-tags"
          type="text"
          className="input w-full"
          value={tagsRaw}
          onChange={(e) => { setTagsRaw(e.target.value); }}
          placeholder="ai, ml, infra"
        />
        <p className="help">comma-separated, e.g. <code>ai, ml, infra</code></p>
      </div>

      <div>
        <label className="label" htmlFor="src-expertise">expertise</label>
        <textarea
          id="src-expertise"
          className="input-textarea w-full"
          rows={2}
          value={state.expertise ?? ''}
          onChange={(e) => { setExpertise(e.target.value); }}
          placeholder="what does this source cover?"
        />
        <p className="help">free-text — surfaced under the source name in the table</p>
      </div>

      <details className="border border-bg-line rounded">
        <summary className="cursor-pointer px-3 py-2 text-2xs font-mono text-ink-muted uppercase tracking-wider hover:text-ink">
          other URLs / handles (optional)
        </summary>
        <div className="px-3 py-3 space-y-3 border-t border-bg-line">
          {OPTIONAL_URL_FIELDS.map(([field]) => {
            // Hide the field that's currently shown as the required input above
            // to avoid two inputs editing the same column.
            if (requiredUrlField === field) return null;
            const value = (state[field] as string | null) ?? '';
            return (
              <div key={field}>
                <label className="label" htmlFor={`src-${field}`}>{field}</label>
                <input
                  id={`src-${field}`}
                  type="text"
                  className="input w-full"
                  value={value}
                  onChange={(e) => { setOptionalText(field, e.target.value); }}
                />
              </div>
            );
          })}
        </div>
      </details>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} disabled={busy} className="btn-ghost">
          Cancel
        </button>
        <button type="submit" disabled={busy} className="btn-primary">
          {busy ? '…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
